import { Button } from '@/app/common/components/button/Button'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { Input } from '@/app/common/components/input/Input'
import FormErrorMessage from '@/common/components/FormErrorMessage'
import SimpleSelector from '@/common/components/SimpleSelector'
import { ChevronDownIcon } from '@/app/common/icons/arrows'
import { EditIconGeneral } from '@/app/common/icons/general'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group'
import { useToast } from '@/shad/ui/toast/use-toast'
import useUpdateBankAccount from '@/queries/bank_accounts/hooks/useUpdateBankAccount'
import { BankAccount, Processor } from '@/queries/bank_accounts/schema'

const EditBankAccountFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  processor_id: z
    .number({ required_error: 'Processor is required' })
    .min(1, 'Processor is required'),
  status: z.union([z.literal(0), z.literal(1)]),
})
type EditBankAccountFormValues = z.infer<typeof EditBankAccountFormSchema>

interface EditBankAccountDialogProps {
  bankAccount: BankAccount
  programId: number
  allProcessors: Processor[]
  takenProcessorIds: Set<number>
  onSuccess: () => void
}

function EditBankAccountDialog({
  bankAccount,
  programId,
  allProcessors,
  takenProcessorIds,
  onSuccess,
}: EditBankAccountDialogProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { toast } = useToast()

  const editableProcessors = allProcessors.filter(
    (p) =>
      !takenProcessorIds.has(p.id) ||
      p.id === bankAccount.v2_locations_processors_id
  )

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<EditBankAccountFormValues>({
    resolver: zodResolver(EditBankAccountFormSchema),
    defaultValues: {
      name: bankAccount.name,
      processor_id: bankAccount.v2_locations_processors_id,
      status: bankAccount.status as 0 | 1,
    },
  })

  useEffect(() => {
    if (!isOpen) return
    reset({
      name: bankAccount.name,
      processor_id: bankAccount.v2_locations_processors_id,
      status: bankAccount.status as 0 | 1,
    })
  }, [isOpen, bankAccount, reset])

  const updateMutation = useUpdateBankAccount({
    onSuccess: () => {
      toast({ title: 'Bank account updated successfully' })
      onSuccess()
      setIsOpen(false)
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Error updating bank account',
        description: error.message,
      })
    },
  })

  const onSubmit = handleSubmit((data) => {
    updateMutation.mutate({
      id: bankAccount.id,
      program_id: programId,
      name: data.name,
      v2_locations_processors_id: data.processor_id,
      status: data.status,
    })
  })

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) reset()
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          aria-label={`Edit bank account ${bankAccount.name}`}
          title="Edit bank account"
        >
          <EditIconGeneral />
        </Button>
      </DialogTrigger>
      <DialogContent className="space-y-4">
        <DialogTitle>Edit Bank Account</DialogTitle>
        <form onSubmit={onSubmit} noValidate className="space-y-3">
          <div>
            <label className="block text-sm font-bold leading-6 text-gray-900 mb-2">
              Name<span className="text-danger ml-1">*</span>
            </label>
            <Input
              {...register('name')}
              type="text"
              placeholder="Enter bank account name"
            />
            <FormErrorMessage message={errors.name?.message} />
          </div>

          <div>
            <label className="block text-sm font-bold leading-6 text-gray-900 mb-2">
              Processor<span className="text-danger ml-1">*</span>
            </label>
            <Controller
              control={control}
              name="processor_id"
              render={({ field }) => {
                const processorOptions = editableProcessors.map((p) => ({
                  id: p.id,
                  name: p.clientId
                    ? `${p.name} - Client ID ${p.clientId}`
                    : `${p.name} - MID ${p.merchantId}`,
                }))
                return (
                  <SimpleSelector
                    list={processorOptions}
                    selected={
                      processorOptions.find((p) => p.id === field.value) ?? null
                    }
                    setSelected={(p) => field.onChange(p.id)}
                    icon={<ChevronDownIcon className="text-lg" />}
                    placeholder={
                      processorOptions.length === 0
                        ? 'No available processors'
                        : 'Select a processor'
                    }
                  />
                )
              }}
            />
            <FormErrorMessage message={errors.processor_id?.message} />
          </div>

          <div>
            <label className="block text-sm font-bold leading-6 text-gray-900 mb-2">
              Status<span className="text-danger ml-1">*</span>
            </label>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <RadioGroup
                  value={String(field.value)}
                  onValueChange={(v) => field.onChange(Number(v))}
                  className="flex flex-col space-y-1 mt-1"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="1" id="edit-status-active" />
                    <label htmlFor="edit-status-active" className="text-sm">
                      Active
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="0" id="edit-status-inactive" />
                    <label htmlFor="edit-status-inactive" className="text-sm">
                      Inactive
                    </label>
                  </div>
                </RadioGroup>
              )}
            />
            {errors.status && (
              <p className="text-sm text-red-500 mt-1">
                {errors.status.message}
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsOpen(false)
                reset()
              }}
            >
              Cancel
            </Button>
            <SubmitButton
              status={updateMutation.status}
              buttonTxt="Save Changes"
            />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default EditBankAccountDialog
